import axios from 'axios';
import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Visor de informes de Power BI (#318, A-46) — contrato de ReportesController
// (GET api/reportes/{nombre}/embed). La API genera el token de embed de solo lectura con el
// service principal; el navegador solo lo usa. Los filtros llegan ya como objetos del SDK.
// Panel de utilidades (#319): refrescos de los datasets del informe y encendido de la capacidad.
// ═══════════════════════════════════════════════════════════════

export const RECURSO_REPORTES_VER = 'Reportes.Ver';
export const RECURSO_REPORTES_GESTIONAR = 'Reportes.Gestionar';

/** Lo que necesita el navegador para mostrar el informe. */
export interface InformeEmbebido {
  idInforme: string;
  titulo: string;
  embedUrl: string;
  token: string;
  /** Cuándo caduca el token (ISO 8601). */
  expira: string;
  /** Filtros del SDK de JavaScript, ya como objetos: se pasan tal cual, nunca se evalúan. */
  filtros: object[];
  /** Los botones del panel de utilidades que marca el catálogo para este informe, y lo que enseña su panel. */
  utilidades: UtilidadesInforme;
}

export interface UtilidadesInforme {
  actualizar: boolean;
  segundoDataset: boolean;
  /** Si el visor enseña el botón info, que abre el panel del informe (#386). */
  info?: boolean;
  responsable?: string | null;
  /** Lo que tardó el último refresco correcto, en segundos: el progreso del siguiente se estima con él. */
  segundosActualizacion?: number | null;
  /** Lo mismo, del segundo dataset (#406). */
  segundosActualizacion2?: number | null;
  /** El texto del enlace al segundo nivel de la ventana info (#406). Sin él, uno por defecto. */
  leyendaSegundoDataset?: string | null;
}

/** Un informe del listado de Reportes (#393): su nombre abre el visor. */
export interface InformeListado {
  nombre: string;
  titulo: string;
  responsable: string | null;
}

export type DatasetInforme = 'principal' | 'segundo';

export type EstadoRefresco = 'EnCurso' | 'Completado' | 'Fallido' | 'Desconocido';

/** El último refresco de un dataset. Las fechas, en ISO 8601; sin fin, sigue en curso. */
export interface RefrescoDataset {
  estado: EstadoRefresco;
  inicio: string | null;
  fin: string | null;
}

/** El último refresco de cada dataset del informe; null si Power BI no tiene ninguno. */
export interface RefrescosInforme {
  principal: RefrescoDataset | null;
  segundo: RefrescoDataset | null;
  /** Lo que tardó el último refresco correcto de cada dataset, en segundos; la API lo guarda al leerlo (#386, #406). */
  segundosActualizacion?: number | null;
  segundosActualizacion2?: number | null;
}

/** Los datos editables de un informe del catálogo (#411). */
export interface DatosInforme {
  activo: boolean;
  nombre: string | null;
  titulo: string | null;
  responsable: string | null;
  guidReporte: string;
  guidDataset2: string | null;
  leyendaDataset2: string | null;
  accesoGeneral: boolean;
  filtroDelegados: boolean;
  filtroDelegaciones: boolean;
  filtroGerenciasZona: boolean;
  filtroProvincias: boolean;
  filtroPersonalizado: string | null;
  botonActualizar: boolean;
  botonInfo: boolean;
}

/** Una fila del catálogo, activa o no (#411): sus datos editables y los de solo lectura. */
export interface InformeCatalogo extends DatosInforme {
  id: number;
  segundosActualizacion: number | null;
  segundosActualizacion2: number | null;
  /** Cuándo se dio de alta (ISO 8601). */
  fechaAlta: string | null;
  usuarioAlta: string | null;
}

/** Un empleado, en los accesos nominales de un informe o en el buscador para añadir uno. */
export interface EmpleadoDeAcceso {
  idEmpleado: number;
  nombre: string;
  email: string;
  activo: boolean;
}

/** Lo que enseña el aviso antes de borrar un informe. */
export interface ImpactoBorrado {
  accesosNominales: number;
  accesosEnLog: number;
}

/** Lo opcional al abrir un informe. */
export interface OpcionesInforme {
  /** La operación de los enlaces del GdV (#347): el informe se filtra además por ella. */
  operacion?: string;
  /** Para renovar el token de un informe que ya está abierto (#338): la API no lo cuenta como otro acceso. */
  renovacion?: boolean;
}

export const abrirInforme = async (nombre: string, opciones: OpcionesInforme = {}): Promise<InformeEmbebido> => {
  const params = {
    ...(opciones.operacion !== undefined ? { operacion: opciones.operacion } : {}),
    ...(opciones.renovacion ? { renovacion: true } : {}),
  };
  const res = await api.get(`/api/reportes/${encodeURIComponent(nombre)}/embed`, Object.keys(params).length ? { params } : undefined);
  return res.data;
};

/** Los informes activos que el usuario puede abrir, ordenados por título (#393). */
export const listarInformes = async (): Promise<InformeListado[]> => {
  const res = await api.get('/api/reportes');
  return res.data;
};

export const leerRefrescos = async (nombre: string): Promise<RefrescosInforme> => {
  const res = await api.get(`/api/reportes/${encodeURIComponent(nombre)}/refrescos`);
  return res.data;
};

/** Pide el refresco de un dataset del informe; su avance se sigue con `leerRefrescos`. */
export const actualizarDataset = async (nombre: string, dataset: DatasetInforme): Promise<void> => {
  await api.post(`/api/reportes/${encodeURIComponent(nombre)}/actualizar`, null, { params: { dataset } });
};

/** Enciende la capacidad de Power BI fuera de su horario. Tarda alrededor de un minuto. */
export const encenderPowerBi = async (): Promise<void> => {
  await api.post('/api/reportes/encender');
};

// ─── Catálogo (#411): todo exige Reportes.Gestionar ───

/** Todas las filas del catálogo, activas o no. */
export const listarCatalogo = async (): Promise<InformeCatalogo[]> => {
  const res = await api.get('/api/reportes/catalogo');
  return res.data;
};

export const crearInforme = async (datos: DatosInforme): Promise<InformeCatalogo> => {
  const res = await api.post('/api/reportes/catalogo', datos);
  return res.data;
};

export const actualizarInforme = async (id: number, datos: DatosInforme): Promise<InformeCatalogo> => {
  const res = await api.put(`/api/reportes/catalogo/${id}`, datos);
  return res.data;
};

export const leerImpactoBorrado = async (id: number): Promise<ImpactoBorrado> => {
  const res = await api.get(`/api/reportes/catalogo/${id}/borrado`);
  return res.data;
};

/** Borra el informe, para siempre. `confirmacion` es lo que ha escrito quien lo borra: la API lo comprueba otra vez. */
export const borrarInforme = async (id: number, confirmacion: string): Promise<void> => {
  await api.delete(`/api/reportes/catalogo/${id}`, { params: { confirmacion } });
};

export const leerAccesos = async (id: number): Promise<EmpleadoDeAcceso[]> => {
  const res = await api.get(`/api/reportes/catalogo/${id}/accesos`);
  return res.data;
};

/** Da acceso nominal al informe a un empleado activo. Devuelve la lista como queda. */
export const anadirAcceso = async (id: number, idEmpleado: number): Promise<EmpleadoDeAcceso[]> => {
  const res = await api.put(`/api/reportes/catalogo/${id}/accesos/${idEmpleado}`);
  return res.data;
};

/** Quita el acceso nominal al informe a un empleado. Devuelve la lista como queda. */
export const quitarAcceso = async (id: number, idEmpleado: number): Promise<EmpleadoDeAcceso[]> => {
  const res = await api.delete(`/api/reportes/catalogo/${id}/accesos/${idEmpleado}`);
  return res.data;
};

/** Empleados activos por nombre o email, para añadirlos a los accesos de un informe. */
export const buscarEmpleados = async (texto: string): Promise<EmpleadoDeAcceso[]> => {
  const res = await api.get('/api/reportes/catalogo/empleados', { params: { texto } });
  return res.data;
};

/** El mensaje para el usuario de un error de la API, sin detalles internos (los da el servidor). */
export const mensajeDeError = (
  error: unknown,
  porDefecto = 'No se ha podido abrir el informe. Inténtalo de nuevo en unos minutos.',
): string => {
  if (axios.isAxiosError(error)) {
    const detalle = (error.response?.data as { detail?: string } | undefined)?.detail;
    if (detalle) return detalle;
    if (error.response?.status === 403) return 'No tienes acceso a este informe.';
  }
  return porDefecto;
};
